import { useState, useCallback } from 'react';

interface CursorPaginationState {
  cursors: (string | null)[];
  currentIndex: number;
}

interface UseCursorPaginationReturn {
  currentCursor: string | null;
  currentPage: number;
  canGoBack: boolean;
  canGoForward: boolean;
  isNextCursorCached: (nextCursor: string | null) => boolean;
  goToNextPage: (nextCursor: string | null) => void;
  goToPreviousPage: () => void;
  reset: () => void;
}

export const useCursorPagination = (): UseCursorPaginationReturn => {
  const [state, setState] = useState<CursorPaginationState>({
    cursors: [null], // start with null for first page
    currentIndex: 0,
  });

  const currentCursor = state.cursors[state.currentIndex];
  const currentPage = state.currentIndex + 1;
  const canGoBack = state.currentIndex > 0;
  const canGoForward = state.currentIndex < state.cursors.length - 1;

  const isNextCursorCached = useCallback(
    (nextCursor: string | null) => {
      const nextIndex = state.currentIndex + 1;
      return nextIndex < state.cursors.length && state.cursors[nextIndex] === nextCursor;
    },
    [state.currentIndex, state.cursors]
  );

  const goToNextPage = useCallback((nextCursor: string | null) => {
    setState((prevState) => {
      // check if we're already at the next position in our cursor stack
      const nextIndex = prevState.currentIndex + 1;

      if (nextIndex < prevState.cursors.length && prevState.cursors[nextIndex] === nextCursor) {
        return {
          ...prevState,
          currentIndex: nextIndex,
        };
      }

      // otherwise, we're adding a new page to our stack
      const newCursors = [...prevState.cursors];

      // only add the next cursor if it's different from the current one and not null
      if (nextCursor && nextCursor !== prevState.cursors[prevState.currentIndex]) {
        newCursors.push(nextCursor);

        return {
          cursors: newCursors,
          currentIndex: prevState.currentIndex + 1,
        };
      }

      return prevState;
    });
  }, []);

  const goToPreviousPage = useCallback(() => {
    setState((prevState) => {
      if (prevState.currentIndex > 0) {
        return {
          ...prevState,
          currentIndex: prevState.currentIndex - 1,
        };
      }

      return prevState;
    });
  }, []);

  const reset = useCallback(() => {
    setState({
      cursors: [null],
      currentIndex: 0,
    });
  }, []);

  return {
    currentCursor,
    currentPage,
    canGoBack,
    canGoForward,
    isNextCursorCached,
    goToNextPage,
    goToPreviousPage,
    reset,
  };
};
